/**
 * Auth store — JWT + user + lista context owners + CO activo.
 *
 * Patrón G.5.X1 cierre 2026-05-22: login no auto-asigna co_canonico_id
 * cuando hay >1 CO. La PWA debe redirigir a /select-co obligatorio.
 *
 * Persistencia: localStorage para token + user.id + active_co_id.
 * Expiración: NO hay refresh de token. En cada apiCall, un 401 → logout + redirect /login
 * (el JWT es bearer opaco de vida 24h; al expirar/revocarse se re-loguea, no se refresca).
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'
import { tokenExpirado } from '@/lib/jwt'
import { logAarpia } from '@/lib/log'

export interface User {
  id: string
  email: string
  nombre: string | null
  nivel: string
  avatar_url: string | null
}

export interface ContextOwner {
  id: string
  nombre: string
  entidad_de_negocio_id: string | null
  nivel: string
  es_propietario: boolean
  vinculo_id?: string
  fecha_alta?: string
}

interface AuthState {
  token: string | null
  user: User | null
  contextOwners: ContextOwner[]
  activeCoId: string | null
  requiresCoSelection: boolean
  loading: boolean
  error: string | null

  login: (email: string, password: string) => Promise<void>
  selectCo: (coId: string) => Promise<void>
  loadMyContextOwners: () => Promise<void>
  logout: () => void
  isAuthenticated: () => boolean
}

interface LoginResponse {
  token: string
  user: User
  context_owners: ContextOwner[]
  requires_co_selection: boolean
}

interface SwitchCoResponse {
  token: string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      contextOwners: [],
      activeCoId: null,
      requiresCoSelection: false,
      loading: false,
      error: null,

      async login(email: string, password: string) {
        set({ loading: true, error: null })
        try {
          const r = await api.post<LoginResponse>('/auth/login', null, { email, password })
          set({
            token: r.token,
            user: r.user,
            contextOwners: r.context_owners || [],
            requiresCoSelection: r.requires_co_selection === true,
            activeCoId: r.requires_co_selection ? null : (r.context_owners[0]?.id ?? null),
            loading: false,
          })
        } catch (e: any) {
          set({ loading: false, error: e.message || 'Login fallido' })
          throw e
        }
      },

      async selectCo(coId: string) {
        set({ loading: true, error: null })
        try {
          const token = get().token
          const r = await api.post<SwitchCoResponse>('/auth/switch-co', token, { context_owner_id: coId })
          set({
            token: r.token,
            activeCoId: coId,
            requiresCoSelection: false,
            loading: false,
          })
          // G.3.7 multi-CO REAL: invalidar cache catálogo porque las
          // entidades visibles del CO Tenant cambian con el switch.
          try {
            const { useCatalogStore } = await import('./catalog')
            useCatalogStore.getState().reset()
          } catch {
            // ignore — store puede no estar inicializado en SSR/test
          }
        } catch (e: any) {
          set({ loading: false, error: e.message || 'Switch CO fallido' })
          throw e
        }
      },

      async loadMyContextOwners() {
        const token = get().token
        if (!token) return
        try {
          const r = await api.get<{ data: ContextOwner[] }>('/mis-context-owners', token)
          set({ contextOwners: r.data || [] })
        } catch {
          // silent fail — la UI mostrará lista anterior
        }
      },

      logout() {
        // DC-5b (8f3a2596): PURGA INTEGRAL de la sesión — el JWT stale de un usuario
        // anterior persistido en storage arrancaba el shell entero (7×401 + SSE en
        // bucle) en la siguiente visita. Capturar el uid ANTES de limpiar el estado.
        const uid = get().user?.id || null
        set({
          token: null,
          user: null,
          contextOwners: [],
          activeCoId: null,
          requiresCoSelection: false,
          error: null,
        })
        // Cleanup stores dependientes
        try {
          import('./catalog').then((m) => m.useCatalogStore.getState().reset())
          import('./chat').then((m) => m.useChatStore.getState().reset())
          import('./branding').then((m) => m.useBrandingStore.getState().reset())
        } catch { /* ignore */ }
        // Caché optimista de preferencias del usuario saliente (+ clave legacy sin uid).
        // El server es la fuente de verdad — el caché se reconstruye al re-login.
        // Theme/sidebar NO se purgan: son preferencia del dispositivo, no de la sesión.
        try {
          if (uid) localStorage.removeItem(`arquitecto-prefs-tablas:${uid}`)
          localStorage.removeItem('arquitecto-prefs-tablas')
        } catch { /* ignore */ }
      },

      isAuthenticated() {
        return !!get().token
      },
    }),
    {
      name: 'arquitecto-auth',
      // contextOwners se persiste: tras recargar la PWA (común en móvil) el nombre del CO
      // activo debe seguir disponible (lo usa la cabecera de auditoría del informe, el
      // OrgSwitcher, etc.) sin re-login. Son pocos COs por usuario; coste despreciable.
      partialize: (s) => ({ token: s.token, user: s.user, activeCoId: s.activeCoId, contextOwners: s.contextOwners }),
      // DC-5a (8f3a2596): GATE DE EXP AL REHIDRATAR — un JWT expirado persistido (p.ej.
      // de otro usuario en una sesión vieja del mismo navegador) NO arranca sesión: el
      // merge lo descarta DURANTE la hidratación, antes de que el shell dispare
      // catálogo/asyncapi/SSE con él (las 7×401 + bucle SSE de la auditoría en vivo).
      // El servidor sigue siendo la autoridad (firma/revocación); esto corta lo
      // obviamente muerto. `merge` y no onRehydrateStorage: determina el estado
      // resultante de forma síncrona, sin mutación post-hoc que no notifica.
      merge: (persisted: any, current) => {
        if (persisted?.token && tokenExpirado(persisted.token)) {
          logAarpia.warn('Sesión persistida expirada — purgada al arrancar (sin llamadas con token muerto).')
          return current // estado limpio: sin token/user/CO del storage
        }
        return { ...current, ...(persisted || {}) }
      },
    },
  ),
)
